import React from 'react';
import { GoogleMap, useJsApiLoader } from '@react-google-maps/api';
import { GOOGLE_MAP_KEY, GOOGLE_MAPS_LIBRARIES } from '@src/core/config';
import { ArcGoogleMapProps } from './types';
import { ArcStack, ArcCircularProgress } from '@src/core/ui';
import { useDispatch, useSelector } from 'react-redux';
import { selectMapCenter, selectMapType, selectMapZoom } from '@src/core/controllers/map/redux/selectors';
import { setMapType, setMapZoom } from '@src/core/controllers/map/redux/actions';
import { MapTypeStatus } from '@src/core/controllers/map/entities';
import Toolbar from '@src/core/controllers/map/components/toolbar';
import RulerLayer from '@src/core/controllers/map/components/ruler-layer';

const ArcGoogleMap: React.FC<ArcGoogleMapProps> = (props) => {
  const { children, hideToolbar = false, hideMapControls = false, ...mapProps } = props;

  const dispatch = useDispatch();
  const zoom = useSelector(selectMapZoom);
  const center = useSelector(selectMapCenter);
  const mapTypeId = useSelector(selectMapType);
  const [isLoaded, setIsLoaded] = React.useState(false);
  
  const { isLoaded: apiLoaded } = useJsApiLoader({
    googleMapsApiKey: GOOGLE_MAP_KEY,
    libraries: GOOGLE_MAPS_LIBRARIES,
  });

  function onZoomChanged() {
    dispatch(setMapZoom(this.zoom as number));
  }
  function onMapTypeIdChanged() {
    dispatch(setMapType(this.mapTypeId));
  }

  React.useEffect(() => {
    if (isLoaded) {
      dispatch(setMapType(MapTypeStatus.HYBRID));
    }
  }, [isLoaded]);

  // A fresh options object literal on every render makes @react-google-maps/api
  // call map.setOptions() each render, which re-creates the native Map/Satellite
  // control and makes it flicker during frequent re-renders (e.g. live telemetry
  // updates while "Go Home" is active). Memoize so the reference stays stable.
  const options = React.useMemo<google.maps.MapOptions>(
    () => ({
      rotateControl: false,
      scaleControl: false,
      streetViewControl: false,
      disableDoubleClickZoom: true,
      panControl: false,
      mapTypeControl: !hideMapControls,
      fullscreenControl: !hideMapControls,
      zoomControl: !hideMapControls,
    }),
    [hideMapControls],
  );

  const mapContainerStyle = React.useMemo(() => ({ width: '100%', height: '100%' }), []);

  if (!apiLoaded) {
    return (
      <ArcStack
        width="100%"
        height="100%"
        alignItems="center"
        justifyContent="center"
        bgcolor="background.default">
        <ArcCircularProgress />
      </ArcStack>
    );
  }

  return (
    <ArcStack width="100%" height="100%" position="relative">
      {!hideToolbar && <Toolbar />}

      <GoogleMap
        zoom={zoom}
        center={center}
        mapTypeId={mapTypeId}
        onZoomChanged={onZoomChanged}
        onLoad={() => setIsLoaded(true)}
        onMapTypeIdChanged={onMapTypeIdChanged}
        mapContainerStyle={mapContainerStyle}
        options={options}
        {...mapProps}>

        {children}
        <RulerLayer />

      </GoogleMap>
    </ArcStack>
  );
};

export default ArcGoogleMap;
